/**
 * @file WmsPickingTaskPage.tsx
 * @description Экран задания на сборку (phase19.14, экран B шага 0; эталон
 * складского документа — phase19.23; раскладка «как в приёмке» — phase19.24;
 * общий каркас `DocumentLayout` — этап 1, шаг 1).
 *
 * Сценарий: «Взять в работу» → фокус в сканер → скан или «Подбор» позиции →
 * количество → «Отметить» → «Провести сборку» → заказ уходит в «Собран».
 *
 * Раскладка как у приёмки: шапка документа → полосы-предупреждения → позиции
 * слева и панель «Отметить позицию» справа. У закрытого и чужого задания
 * панели нет (правило §5.2) — вместо неё поле поиска над списком. На телефоне
 * главное действие живёт в sticky-панели снизу — правило warehouse-UI.
 *
 * Данные, поиск, фокус и диалоги — в `usePickingTaskScreen`, доступность
 * исходов — в `buildPickingActionsState`: странице остаются раскладка, права
 * и подача.
 *
 * phase19.31 (зона «Склад», минимум по SC50): шапка — в верхней панели
 * («назад», номер, статус, «⋯», одно главное действие); своя липкая панель
 * прогресса `PickProgressBar` убрана — на телефоне главное действие в общей
 * нижней полосе каркаса, прогресс — в панели отметки и плитке «Собрано».
 */

import { Loader2 } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router";

import { useAuth, useHasPermission } from "../../../auth/hooks";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { DocumentLayout } from "../../../components/ui/DocumentLayout";
import { PickMarkPanel } from "../../../components/wms/picking/PickMarkPanel";
import { LotMismatchDialog } from "../../../components/wms/picking/LotMismatchDialog";
import { PickingRebuildButton } from "../../../components/wms/picking/PickingRebuildButton";
import { buildPickingActionsState } from "../../../components/wms/picking/picking-actions-state";
import { PickingTaskDialogs } from "../../../components/wms/picking/PickingTaskDialogs";
import { PickingTaskDocHeader } from "../../../components/wms/picking/PickingTaskDocHeader";
import { PickingTaskItemsSection } from "../../../components/wms/picking/PickingTaskItemsSection";
import { PickingTaskNotices } from "../../../components/wms/picking/PickingTaskNotices";
import { usePickListPrint } from "../../../components/wms/picking/usePickListPrint";
import { usePickingTaskScreen } from "../../../components/wms/picking/usePickingTaskScreen";
import { canRebuildTask, formatPickItemName } from "../../../lib/api-picking";
import { formatItemQty } from "../../../lib/picking-format";

export function WmsPickingTaskPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const { me } = useAuth();
  const canExecute = useHasPermission("SHOP_WMS_PICKING");
  const canManage = useHasPermission("SHOP_WMS_MANAGE");
  const canOpenOrder = useHasPermission("SHOP_VIEW_ORDERS");
  const userId = me?.user.id ?? null;
  const screen = usePickingTaskScreen(id, { canExecute, userId });
  const { pick, search } = screen;
  // Откуда пришли: из журнала (`?from=history`) возвращаем в журнал, иначе в
  // очередь — иначе «Назад» после просмотра закрытого задания теряет список.
  const [searchParams] = useSearchParams();
  const fromHistory = searchParams.get("from") === "history";
  const backTo = `/shop/${slug}/wms/picking${fromHistory ? "?view=history" : ""}`;
  const { printingId, print } = usePickListPrint();

  const task = pick.task;

  if (pick.loading && !task) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!task) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert severity="error">{pick.loadError ?? "Задание не найдено"}</Alert>
        <Button variant="outline" asChild>
          <Link to={backTo}>{fromHistory ? "К журналу заданий" : "К очереди заданий"}</Link>
        </Button>
      </div>
    );
  }

  const shortageLines = task.items
    .filter((i) => Number(i.shortageQty) > 0)
    .map((i) => `${formatPickItemName(i)} — ${formatItemQty(i.shortageQty, i.qtyPlanned, i.unit)}`);
  // Пересборку разрешает сервер (`canRebuild`): в работе — только если строк нет.
  const rebuildable = canManage && canRebuildTask(task);
  const state = buildPickingActionsState(task, screen.editable);
  const openTaskShortage = () => screen.setDialog({ kind: "taskShortage" });
  const openComplete = () => screen.setDialog({ kind: "complete" });
  const rebuildAction = rebuildable ? (
    <PickingRebuildButton onRebuild={() => void pick.rebuild()} busy={pick.busy} />
  ) : null;

  return (
    <DocumentLayout
      header={
        <PickingTaskDocHeader
          task={task}
          back={{ to: backTo, label: fromHistory ? "К журналу заданий" : "К очереди заданий" }}
          currentUserId={userId}
          slug={slug}
          canOpenOrder={canOpenOrder}
          canManage={canManage}
          canClaim={screen.canClaim}
          canComplete={state.canComplete}
          canShortage={state.canShortage}
          shortageCount={state.shortageCount}
          completeHint={state.completeHint}
          shortageHint={state.shortageHint}
          busy={pick.busy}
          onClaim={() => void pick.claim()}
          onComplete={openComplete}
          onShortage={openTaskShortage}
          onRelease={() => void pick.release()}
          onCancel={() => void pick.cancel()}
          onRebuild={() => void pick.rebuild()}
          onPrint={() => void print(task)}
          printing={printingId === task.id}
        />
      }
      notices={
        <PickingTaskNotices
          task={task}
          canExecute={canExecute}
          mine={screen.mine}
          slug={slug}
          canOpenOrder={canOpenOrder}
        />
      }
      main={
        <PickingTaskItemsSection
          task={task}
          screen={screen}
          rebuildAction={rebuildAction}
          printing={printingId === task.id}
          onPrint={() => void print(task)}
        />
      }
      aside={
        screen.editable ? (
          <PickMarkPanel
            items={task.items}
            busy={pick.busy}
            scanRef={screen.scanRef}
            onSubmitScan={screen.submitScan}
            onQueryChange={search.changeQuery}
            onMark={screen.setQty}
            scannedItemId={pick.scannedItemId}
            picked={task.pickedCount}
            total={task.itemsCount}
            shortageCount={state.shortageCount}
            binRequest={pick.binRequest}
          />
        ) : undefined
      }
      dialogs={
        <>
        {/* phase19.22 (W17-9c): другая партия — заменить только с причиной. */}
        <LotMismatchDialog pick={pick} onDone={screen.focusScan} />
        <PickingTaskDialogs
          state={screen.dialog}
          task={task}
          shortageLines={shortageLines}
          busy={pick.busy}
          error={pick.actionError}
          onClose={screen.closeDialog}
          onSetQty={pick.setQty}
          onItemShortage={pick.markShortage}
          onComplete={pick.complete}
          onTaskShortage={pick.reportShortage}
        />
        </>
      }
    />
  );
}
